import { useCallback, useEffect, useMemo, useState } from "react";
import HrmsFilters from '../../components/hrms/HrmsFilterPanel';
import { matchesHrmsFilters } from '../../components/hrms/hrmsFilterUtils';
import {
  FiAlertCircle,
  FiCalendar,
  FiCheck,
  FiCheckCircle,
  FiClock,
  FiFilter,
  FiLoader,
  FiRefreshCw,
  FiSearch,
  FiX,
  FiXCircle,
} from "react-icons/fi";
import { apiRequest } from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import Pagination from "../Employees/components/Pagination";

const ITEMS_PER_PAGE = 10;

const STATUS_OPTIONS = [
  { value: "ALL", label: "All Requests" },
  { value: "PENDING", label: "Pending" },
  { value: "APPROVED", label: "Approved" },
  { value: "REJECTED", label: "Rejected" },
  { value: "CANCELLED", label: "Cancelled" },
];

export default function AttendanceCorrections() {
  const { accessToken } = useAuth();

  const [requests, setRequests] = useState([]);
  const [filter, setFilter] = useState("ALL");
  const [searchTerm, setSearchTerm] = useState("");
  const [hrmsFilters, setHrmsFilters] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);
  const [error, setError] = useState("");
  const [reviewRequest, setReviewRequest] = useState(null);
  const [reviewAction, setReviewAction] = useState("");
  const [reviewComment, setReviewComment] = useState("");

  const loadRequests = useCallback(async () => {
    if (!accessToken) return;

    setIsLoading(true);
    setError("");

    try {
      const query = filter === "ALL" ? "" : `?status=${filter}`;

      const response = await apiRequest(`/attendance/corrections${query}`, {
        accessToken,
      });

      setRequests(
        Array.isArray(response)
          ? response
          : response?.requests || response?.data || [],
      );
    } catch (requestError) {
      setRequests([]);
      setError(
        requestError.message ||
          "Could not load attendance correction requests.",
      );
    } finally {
      setIsLoading(false);
    }
  }, [accessToken, filter]);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  const normalizedRequests = useMemo(
    () =>
      requests.map((request) => {
        const assignment = request.employee?.assignments?.[0];

        return {
          ...request,
          employeeCode: request.employee?.employeeCode || "—",
          employeeName:
            request.employee?.nickname ||
            request.employee?.fullName ||
            "Unknown Employee",
          officialName: request.employee?.fullName || "Unknown Employee",
          designation: assignment?.designation?.name || "Unassigned",
          department: assignment?.department?.name || "Unassigned",
          branch: assignment?.branch?.name || "Unassigned",
          displayStatus: toTitle(request.status),
          correctionType: getCorrectionType(request),
        };
      }),
    [requests],
  );

  const visibleRequests = useMemo(() => {
    const search = searchTerm.trim().toLowerCase();

    return normalizedRequests.filter((request) =>
      matchesHrmsFilters(request,hrmsFilters) &&
      [
        request.employeeName,
        request.officialName,
        request.employeeCode,
        request.designation,
        request.department,
        request.branch,
        request.reason,
        request.correctionType,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(search),
    );
  }, [normalizedRequests, searchTerm,hrmsFilters]);

  const summary = useMemo(
    () => ({
      total: normalizedRequests.length,
      pending: normalizedRequests.filter(
        (request) => request.status === "PENDING",
      ).length,
      approved: normalizedRequests.filter(
        (request) => request.status === "APPROVED",
      ).length,
      rejected: normalizedRequests.filter(
        (request) => request.status === "REJECTED",
      ).length,
    }),
    [normalizedRequests],
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [filter, searchTerm]);

  const totalPages = Math.max(
    1,
    Math.ceil(visibleRequests.length / ITEMS_PER_PAGE),
  );

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const paginatedRequests = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;

    return visibleRequests.slice(start, start + ITEMS_PER_PAGE);
  }, [visibleRequests, currentPage]);

  const openReviewModal = (request, action) => {
    setReviewRequest(request);
    setReviewAction(action);
    setReviewComment("");
  };

  const closeReviewModal = (force = false) => {
    if (processingId && !force) return;

    setReviewRequest(null);
    setReviewAction("");
    setReviewComment("");
  };

  const submitReview = async () => {
    if (!reviewRequest || !reviewAction) return;

    setProcessingId(reviewRequest.id);
    setError("");

    try {
      await apiRequest(
        `/attendance/corrections/${reviewRequest.id}/${reviewAction}`,
        {
          method: "POST",
          accessToken,
          body: {
            comment: reviewComment.trim() || undefined,
          },
        },
      );

      closeReviewModal(true);
      await loadRequests();
    } catch (requestError) {
      setError(
        requestError.message ||
          `Could not ${reviewAction} this correction request.`,
      );
    } finally {
      setProcessingId(null);
    }
  };

  return (
    <div>
      <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            Attendance Corrections
          </h1>

          <p className="mt-2 text-gray-500">
            Review and process employee attendance correction requests.
          </p>
        </div>

        <button
          type="button"
          onClick={loadRequests}
          disabled={isLoading}
          className="inline-flex items-center justify-center gap-2 rounded-xl bg-red-800 px-5 py-3 font-medium text-white shadow-sm transition hover:bg-red-900 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <FiRefreshCw className={isLoading ? "animate-spin" : ""} />
          Refresh Requests
        </button>
      </div>

      {error ? (
        <div className="mb-6 flex gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          <FiAlertCircle className="mt-0.5 shrink-0" size={18} />
          <p>{error}</p>
        </div>
      ) : null}

      <div className="mb-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <SummaryCard
          label="Total Requests"
          value={summary.total}
          icon={FiCalendar}
          iconClasses="bg-red-50 text-red-700"
        />

        <SummaryCard
          label="Pending Review"
          value={summary.pending}
          icon={FiClock}
          iconClasses="bg-amber-50 text-amber-700"
        />

        <SummaryCard
          label="Approved"
          value={summary.approved}
          icon={FiCheckCircle}
          iconClasses="bg-green-50 text-green-700"
        />

        <SummaryCard
          label="Rejected"
          value={summary.rejected}
          icon={FiXCircle}
          iconClasses="bg-rose-50 text-rose-700"
        />
      </div>

      <div className="mb-6 rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="relative w-full lg:max-w-md">
            <FiSearch
              className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
              size={18}
            />

            <input
              type="text"
              placeholder="Search employee, code, branch or reason..."
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              className="w-full rounded-xl border border-gray-200 py-3 pl-11 pr-4 transition-all focus:border-red-800 focus:outline-none focus:ring-2 focus:ring-red-100"
            />
          </div>

          <div className="flex items-center gap-3">
            <FiFilter className="text-gray-400" size={18} />

            <select
              value={filter}
              onChange={(event) => setFilter(event.target.value)}
              className="min-w-[185px] rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm font-medium text-gray-700 outline-none transition focus:border-red-800 focus:ring-2 focus:ring-red-100"
            >
              {STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="mb-5"><HrmsFilters records={requests} value={hrmsFilters} onChange={next => { setHrmsFilters(next); setCurrentPage(1); }} /></div>
      <div className="mb-4 flex items-center justify-between">
        <p className="text-sm text-gray-500">
          Showing{" "}
          <span className="font-semibold text-gray-900">
            {visibleRequests.length}
          </span>{" "}
          request{visibleRequests.length === 1 ? "" : "s"}
        </p>
      </div>

      {isLoading ? (
        <div className="rounded-2xl border border-gray-100 bg-white py-16 text-center text-gray-500">
          <FiLoader className="mr-2 inline animate-spin text-red-800" />
          Loading correction requests...
        </div>
      ) : paginatedRequests.length ? (
        <>
          <div className="overflow-hidden rounded-3xl border border-gray-100 bg-white shadow-sm">
            <div className="hidden border-b border-gray-100 bg-gray-50 px-6 py-4 text-xs font-semibold uppercase tracking-wider text-gray-500 xl:grid xl:grid-cols-[minmax(240px,1.2fr)_minmax(180px,0.9fr)_minmax(220px,1fr)_minmax(160px,0.7fr)_auto] xl:gap-6">
              <span>Employee</span>
              <span>Attendance Date</span>
              <span>Requested Correction</span>
              <span>Status</span>
              <span className="text-right">Actions</span>
            </div>

            <div className="divide-y divide-gray-100">
              {paginatedRequests.map((request) => (
                <CorrectionRow
                  key={request.id}
                  request={request}
                  processingId={processingId}
                  onApprove={() => openReviewModal(request, "approve")}
                  onReject={() => openReviewModal(request, "reject")}
                />
              ))}
            </div>
          </div>

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={visibleRequests.length}
            itemsPerPage={ITEMS_PER_PAGE}
            onPageChange={setCurrentPage}
            itemLabel="requests"
          />
        </>
      ) : (
        <div className="flex flex-col items-center gap-3 rounded-3xl border border-gray-100 bg-white py-16 text-center shadow-sm">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gray-50 text-gray-400">
            <FiClock size={22} />
          </div>

          <p className="font-medium text-gray-700">
            No correction requests found.
          </p>

          <p className="max-w-sm text-sm text-gray-500">
            Try another request status or clear the search field.
          </p>
        </div>
      )}

      <ReviewModal
        open={Boolean(reviewRequest)}
        request={reviewRequest}
        action={reviewAction}
        comment={reviewComment}
        setComment={setReviewComment}
        onClose={closeReviewModal}
        onConfirm={submitReview}
        isSaving={Boolean(processingId)}
      />
    </div>
  );
}

function CorrectionRow({ request, processingId, onApprove, onReject }) {
  const isPending = request.status === "PENDING";
  const isProcessing = processingId === request.id;

  return (
    <div className="grid gap-5 px-5 py-5 transition-colors hover:bg-red-50/50 xl:grid-cols-[minmax(240px,1.2fr)_minmax(180px,0.9fr)_minmax(220px,1fr)_minmax(160px,0.7fr)_auto] xl:items-center xl:gap-6 xl:px-6">
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-red-700 to-red-900 font-semibold text-white shadow-md">
          {getInitials(request.employeeName)}
        </div>

        <div className="min-w-0">
          <p className="truncate font-semibold text-gray-900">
            {request.employeeName}
          </p>

          {request.employeeName !== request.officialName ? (
            <p className="truncate text-xs text-gray-500">
              Official: {request.officialName}
            </p>
          ) : null}

          <p className="mt-0.5 truncate text-sm text-gray-500">
            {request.employeeCode} · {request.designation}
          </p>

          <p className="mt-0.5 truncate text-xs text-gray-400">
            {request.branch}
          </p>
        </div>
      </div>

      <div>
        <p className="text-sm font-semibold text-gray-900">
          {formatDate(request.attendanceDate)}
        </p>

        <p className="mt-1 text-xs text-gray-500">{request.correctionType}</p>
      </div>

      <div>
        <div className="flex flex-wrap gap-2">
          <TimePill
            label="IN"
            value={
              request.requestedCheckInAt
                ? formatTime(request.requestedCheckInAt)
                : "No change"
            }
            classes="border-green-100 bg-green-50 text-green-800"
          />

          <TimePill
            label="OUT"
            value={
              request.requestedCheckOutAt
                ? formatTime(request.requestedCheckOutAt)
                : "No change"
            }
            classes="border-red-100 bg-red-50 text-red-800"
          />
        </div>

        <p className="mt-2 text-xs leading-5 text-gray-500">
          {request.reason || "No reason supplied."}
        </p>
      </div>

      <div>
        <StatusBadge status={request.displayStatus} />

        {request.reviewComment ? (
          <p className="mt-2 text-xs text-gray-500">
            Note: {request.reviewComment}
          </p>
        ) : null}
      </div>

      <div className="flex gap-2 xl:justify-end">
        {isPending ? (
          <>
            <button
              type="button"
              onClick={onReject}
              disabled={isProcessing}
              className="inline-flex h-10 items-center justify-center gap-2 rounded-xl border border-red-200 px-3 text-sm font-semibold text-red-700 transition hover:bg-red-50 disabled:opacity-60"
            >
              <FiX size={16} />
              Reject
            </button>

            <button
              type="button"
              onClick={onApprove}
              disabled={isProcessing}
              className="inline-flex h-10 items-center justify-center gap-2 rounded-xl bg-green-600 px-3 text-sm font-semibold text-white transition hover:bg-green-700 disabled:opacity-60"
            >
              {isProcessing ? (
                <FiLoader className="animate-spin" size={16} />
              ) : (
                <FiCheck size={16} />
              )}
              Approve
            </button>
          </>
        ) : (
          <span className="text-sm text-gray-400">Reviewed</span>
        )}
      </div>
    </div>
  );
}

function ReviewModal({
  open,
  request,
  action,
  comment,
  setComment,
  onClose,
  onConfirm,
  isSaving,
}) {
  if (!open || !request) return null;

  const isApproval = action === "approve";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-slate-950/50 p-4 backdrop-blur-sm">
      <div className="my-auto w-full max-w-xl overflow-hidden rounded-3xl bg-white shadow-2xl">
        <div className="flex items-start justify-between border-b border-gray-100 px-6 py-6 sm:px-7">
          <div>
            <p
              className={`text-sm font-semibold ${
                isApproval ? "text-green-700" : "text-red-700"
              }`}
            >
              Attendance correction review
            </p>

            <h2 className="mt-1 text-2xl font-bold text-gray-900">
              {isApproval ? "Approve correction?" : "Reject correction?"}
            </h2>

            <p className="mt-2 text-sm text-gray-500">
              {request.employeeName} · {request.employeeCode} ·{" "}
              {formatDate(request.attendanceDate)}
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={isSaving}
            className="flex h-10 w-10 items-center justify-center rounded-xl text-gray-400 transition hover:bg-gray-100 hover:text-gray-700 disabled:opacity-50"
          >
            <FiX size={20} />
          </button>
        </div>

        <div className="space-y-5 p-6 sm:p-7">
          <div className="grid grid-cols-2 gap-3">
            <TimePill
              label="Requested IN"
              value={
                request.requestedCheckInAt
                  ? formatTime(request.requestedCheckInAt)
                  : "No change"
              }
              classes="border-green-100 bg-green-50 text-green-800"
              large
            />

            <TimePill
              label="Requested OUT"
              value={
                request.requestedCheckOutAt
                  ? formatTime(request.requestedCheckOutAt)
                  : "No change"
              }
              classes="border-red-100 bg-red-50 text-red-800"
              large
            />
          </div>

          <div className="rounded-2xl bg-gray-50 p-4">
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-400">
              Employee reason
            </p>

            <p className="mt-2 text-sm leading-6 text-gray-700">
              {request.reason || "No reason supplied."}
            </p>
          </div>

          <div>
            <label className="mb-2 block text-sm font-semibold text-gray-700">
              Review comment{" "}
              <span className="font-normal text-gray-400">(optional)</span>
            </label>

            <textarea
              rows={4}
              value={comment}
              onChange={(event) => setComment(event.target.value)}
              placeholder="Add a note visible to the employee..."
              className="w-full resize-none rounded-2xl border border-gray-200 p-4 text-sm outline-none transition focus:border-red-800 focus:ring-2 focus:ring-red-100"
            />
          </div>
        </div>

        <div className="flex flex-col-reverse gap-3 border-t border-gray-100 px-6 py-5 sm:flex-row sm:justify-end sm:px-7">
          <button
            type="button"
            onClick={onClose}
            disabled={isSaving}
            className="h-11 rounded-xl border border-gray-200 px-5 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>

          <button
            type="button"
            onClick={onConfirm}
            disabled={isSaving}
            className={`inline-flex h-11 items-center justify-center gap-2 rounded-xl px-5 text-sm font-semibold text-white disabled:opacity-60 ${
              isApproval
                ? "bg-green-600 hover:bg-green-700"
                : "bg-red-700 hover:bg-red-800"
            }`}
          >
            {isSaving ? <FiLoader className="animate-spin" /> : null}
            {isApproval ? "Approve Correction" : "Reject Correction"}
          </button>
        </div>
      </div>
    </div>
  );
}

function SummaryCard({ label, value, icon: Icon, iconClasses }) {
  return (
    <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">{label}</p>
          <p className="mt-2 text-3xl font-bold text-gray-900">{value}</p>
        </div>

        <div
          className={`flex h-11 w-11 items-center justify-center rounded-xl ${iconClasses}`}
        >
          <Icon size={20} />
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const styles = {
    Pending: "bg-amber-100 text-amber-800",
    Approved: "bg-green-100 text-green-800",
    Rejected: "bg-red-100 text-red-800",
    Cancelled: "bg-gray-100 text-gray-700",
  };

  return (
    <span
      className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
        styles[status] || "bg-gray-100 text-gray-700"
      }`}
    >
      {status}
    </span>
  );
}

function TimePill({ label, value, classes, large = false }) {
  return (
    <div
      className={`rounded-xl border ${
        large ? "px-4 py-3" : "px-2.5 py-2"
      } ${classes}`}
    >
      <p className="text-[10px] font-bold uppercase tracking-wider opacity-65">
        {label}
      </p>

      <p className={`${large ? "mt-1 text-sm" : "text-xs"} font-bold`}>
        {value}
      </p>
    </div>
  );
}

function getInitials(name) {
  return String(name || "Employee")
    .split(" ")
    .filter(Boolean)
    .map((word) => word[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

function getCorrectionType(request) {
  if (request.requestedStatus) {
    return `Status: ${toTitle(request.requestedStatus)}`;
  }

  if (request.requestedCheckInAt && request.requestedCheckOutAt) {
    return "Check-in & check-out correction";
  }

  if (request.requestedCheckInAt) {
    return "Check-in correction";
  }

  if (request.requestedCheckOutAt) {
    return "Check-out correction";
  }

  return "Attendance correction";
}

function formatDate(value) {
  if (!value) return "—";

  return new Date(value).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function formatTime(value) {
  if (!value) return "—";

  return new Date(value).toLocaleTimeString("en-IN", {
    timeZone: "Asia/Kolkata",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

function toTitle(value = "") {
  return String(value)
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}
